import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ChatProvider, useChat } from "@/contexts/ChatContext";
import { CreditProvider, useCredit } from "@/contexts/CreditContext";
import { NotificationProvider, useNotification } from "@/contexts/NotificationContext";
import { getTranslation } from "@/locales/translations";
import { showToast, customToasts } from "@/utils/toast";
import { toast } from "sonner";
import axios from "axios";

// Extracted Components
import ChatHeader from "@/components/chat/ChatHeader";
import ChatMessages from "@/components/chat/ChatMessages";
import ChatInput from "@/components/chat/ChatInput";
import Sidebar from "@/components/chat/Sidebar";
import ResearchLoadingAnimation from "@/components/chat/ResearchLoadingAnimation";
import RealtimeVoice from "@/components/RealtimeVoice";

// Modal Components
import AuthModal from "@/components/chat/modals/AuthModal";
import SupportModal from "@/components/chat/modals/SupportModal";
import NotificationsModal from "@/components/chat/modals/NotificationsModal";
import CreditLimitModal from "@/components/chat/modals/CreditLimitModal";
import PrivacySettingsModal from "@/components/chat/modals/PrivacySettingsModal";
import AccountModal from "@/components/chat/modals/AccountModal";
import PersonalizationModal from "@/components/chat/modals/PersonalizationModal";
import ImageViewerModal from "@/components/chat/modals/ImageViewerModal";
import DeleteModal from "@/components/chat/modals/DeleteModal";
import CreditPurchaseModal from "@/components/CreditPurchaseModal";

// UI Components (minimal - modals use their own)
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import TurkishCrescent from "../components/TurkishCrescent";
import "../styles/kids-theme.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = '/api';

/**
 * ChatPageContent - Main chat interface component
 * Contains all chat logic and UI state management
 */
function ChatPageContent() {
  const { theme, isDark, toggleTheme } = useTheme();
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const navigate = useNavigate();
  
  // Context hooks
  const { user, authLoading, login, register, guestLogin, logout, refreshUserInfo } = useAuth();
  const { 
    sessions, 
    currentSession, 
    messages, 
    loading, 
    isTyping,
    favorites,
    setSessions,
    setCurrentSession, 
    setMessages,
    setIsTyping,
    loadSessions, 
    loadMessages,
    createSession, 
    deleteSession: deleteSessionContext,
    renameSession,
    loadFavorites,
    toggleFavorite: toggleFavoriteContext
  } = useChat();
  const { creditStatus, fetchCreditStatus, checkCreditLimit } = useCredit();
  const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead } = useNotification();
  
  // UI State
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [showFavorites, setShowFavorites] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Modal States
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showCreditLimitModal, setShowCreditLimitModal] = useState(false);
  const [showPrivacySettingsModal, setShowPrivacySettingsModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showPersonalizationModal, setShowPersonalizationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [showDataRequestConfirm, setShowDataRequestConfirm] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  
  // Modal Tab States
  const [authTab, setAuthTab] = useState("login");
  const [supportTab, setSupportTab] = useState("new");
  
  // Input States
  const [input, setInput] = useState("");
  const [model, setModel] = useState("tc-milli-gpt");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [useLibrary, setUseLibrary] = useState(false);
  
  // Voice States - Realtime Voice
  const [realtimeMode, setRealtimeMode] = useState(false);
  const [messageAudioChunks, setMessageAudioChunks] = useState({}); // messageId -> audioChunks mapping
  const [isReplaying, setIsReplaying] = useState(null); // messageId being replayed
  const [pendingAudioChunks, setPendingAudioChunks] = useState(null); // Audio chunks waiting for message
  const [messageFeedback, setMessageFeedback] = useState({}); // messageId -> feedback ('good' or 'bad')
  
  // Research States
  const [researchStartTime, setResearchStartTime] = useState(null);
  const [researchCreditsUsed, setResearchCreditsUsed] = useState(0);
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const currentAudioRef = useRef(null);
  const audioPlayingRef = useRef(false);
  const isVoiceModeRef = useRef(false);
  const messageImagesRef = useRef({});
  
  // Additional States (minimal - modals manage their own state)
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [creditError, setCreditError] = useState(null);
  
  // Derived State
  const isKidsUser = user && user.user_type === 'kids';
  const trialDaysLeft = user?.is_trial && user?.trial_end_date 
    ? Math.ceil((new Date(user.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  
  // 🎯 Kids kullanıcısı için /study/chat sayfasına yönlendir
  useEffect(() => {
    if (isKidsUser) {
      navigate('/app/study/chat');
    }
  }, [isKidsUser, navigate]);
  
  // Initialize on mount
  useEffect(() => {
    // authLoading true iken bekle - storage henüz kontrol edilmedi
    if (authLoading) return;
    
    if (user) {
      loadSessions(user.id, 'chat').catch(err => console.log('Sessions load failed, continuing...'));
      loadFavorites(user.id).catch(err => console.log('Favorites load failed, continuing...'));
      fetchCreditStatus().catch(err => console.log('Credit status load failed, continuing...'));
      if (!user.email?.includes('guest_')) {
        fetchNotifications(user.id).catch(err => console.log('Notifications load failed, continuing...'));
      }
      
      // URL'den ?buy=credits gelirse kredi modal'ını aç
      const params = new URLSearchParams(window.location.search);
      if (params.get('buy') === 'credits') {
        setShowCreditModal(true);
        // URL'den parametreyi temizle
        window.history.replaceState({}, '', '/app');
      }
    } else {
      setShowAuthModal(true);
    }
  }, [user?.id, authLoading]);
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  // Kids theme effect
  useEffect(() => {
    if (isKidsUser) {
      document.body.classList.add('kids-mode');
      if (isDark) {
        document.body.classList.add('dark');
      }
    } else {
      document.body.classList.remove('kids-mode');
    }
    
    return () => {
      document.body.classList.remove('kids-mode', 'dark');
    };
  }, [isKidsUser, isDark]);
  
  // Handle Agent Result
  const handleAgentResult = useCallback(async (agentData) => {
    // Handle clear loading (for errors)
    if (agentData.clearLoading) {
      setMessages(prev => prev.filter(msg => !msg.isLoading && !msg.isResearchLoading));
      setResearchStartTime(null);
      setResearchCreditsUsed(0);
      return;
    }
    
    // Handle loading animation trigger
    if (agentData.showLoading) {
      if (!currentSession) {
        const newSession = await createSession(user.id, 'Agent Sonucu', 'chat');
        setCurrentSession(newSession);
      }
      
      // Track research start time and credits
      setResearchStartTime(Date.now());
      setResearchCreditsUsed(agentData.credits_used || 30);
      
      // Add and save user message
      if (agentData.user_query) {
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch('/api/messages', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              session_id: currentSession.id,
              role: 'user',
              content: agentData.user_query
            })
          });
          
          if (response.ok) {
            const savedMessage = await response.json();
            const userMsg = {
              id: savedMessage.id,
              role: "user",
              content: agentData.user_query,
              timestamp: savedMessage.timestamp
            };
            setMessages(prev => [...prev, userMsg]);
          }
        } catch (error) {
          console.error('Error saving user message:', error);
          // Still show in UI
          const userMsg = {
            id: 'user-' + Date.now(),
            role: "user",
            content: agentData.user_query,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, userMsg]);
        }
      }
      
      const loadingMsg = {
        id: 'loading-' + Date.now(),
        role: "assistant",
        content: "RESEARCH_LOADING_ANIMATION",
        timestamp: new Date().toISOString(),
        isLoading: true,
        isResearchLoading: true
      };
      setMessages(prev => [...prev, loadingMsg]);
      return; // Don't process further, wait for actual result
    }
    
    if (!currentSession) {
      const newSession = await createSession(user.id, 'Agent Sonucu', 'chat');
      setCurrentSession(newSession);
    }
    
    const result = agentData.result;
    
    // Format agent response based on type
    let formattedResponse = `**${agentData.agent_name} Sonucu:**\n\n`;
    
    // Check if it's a research report (new format)
    if (result.type === 'research_report' && result.report) {
      const report = result.report;
      
      // Add mode badge
      const modeBadge = report.mode === 'deep' ? '🔬 DEEP MODE' : 
                       report.mode === 'pro' ? '⚡ PRO MODE' : '⚡ QUICK MODE';
      formattedResponse += `${modeBadge}\n\n`;
      
      // Add executive summary
      if (report.executiveSummary) {
        formattedResponse += `${report.executiveSummary}\n\n`;
      }
      
      // Add comparative analysis (Deep mode only)
      if (report.comparativeAnalysis) {
        formattedResponse += `**📊 Karşılaştırmalı Analiz**\n\n${report.comparativeAnalysis}\n\n`;
      }
      
      // Add findings
      if (report.findings && report.findings.length > 0) {
        formattedResponse += `**🔍 Detaylı Bulgular**\n\n`;
        report.findings.forEach((finding, index) => {
          formattedResponse += `**${index + 1}. ${finding.title}**\n\n`;
          formattedResponse += `${finding.summary}\n\n`;
        });
      }
      
      // Add risks (if any)
      if (report.risks && report.risks.length > 0) {
        formattedResponse += `**⚠️ Riskler ve Belirsizlikler**\n\n`;
        report.risks.forEach((risk) => {
          formattedResponse += `• ${risk}\n`;
        });
        formattedResponse += '\n';
      }
      
      // Add recommendations
      if (report.recommendations && report.recommendations.length > 0) {
        formattedResponse += `**💡 Öneriler**\n\n`;
        report.recommendations.forEach((rec) => {
          formattedResponse += `${rec}\n\n`;
        });
      }
      
      // Add sources
      if (report.sources && report.sources.length > 0) {
        formattedResponse += `**📚 Kaynaklar (${report.sources.length})**\n\n`;
        report.sources.slice(0, 10).forEach((source, index) => {
          formattedResponse += `${index + 1}. [${source.domain}](${source.url})\n`;
        });
      }
      
      // Add metadata
      if (report.metadata) {
        formattedResponse += `\n---\n`;
        formattedResponse += `📊 ${report.metadata.subQuestionCount} alt soru araştırıldı • `;
        formattedResponse += `${report.metadata.sourceCount} kaynak analiz edildi • `;
        formattedResponse += `Güven skoru: ${report.confidenceScore}%`;
      }
    } 
    // Legacy format (old Perplexity-only)
    else if (result.answer) {
      formattedResponse += `${result.answer}`;
      
      if (result.citations && result.citations.length > 0) {
        formattedResponse += '\n\n**Kaynaklar:**\n';
        result.citations.forEach((citation, index) => {
          formattedResponse += `${index + 1}. ${citation}\n`;
        });
      }
    }
    
    // Remove loading message if exists
    setMessages(prev => prev.filter(msg => !msg.isLoading));
    
    // Save assistant message to database
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: currentSession.id,
          role: 'assistant',
          content: formattedResponse,
          metadata: {
            agent_id: agentData.result.type || 'deep_research',
            agent_name: agentData.agent_name,
            credits_used: agentData.credits_used
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save message');
      }
      
      const savedMessage = await response.json();
      
      // Add assistant message with real ID from database
      const assistantMsg = {
        id: savedMessage.id,
        role: "assistant",
        content: formattedResponse,
        timestamp: savedMessage.timestamp,
        agent_result: result
      };
      
      setMessages(prev => [...prev, assistantMsg]);
      
    } catch (error) {
      console.error('Error saving agent result:', error);
      // Still show message in UI even if save fails
      const assistantMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: formattedResponse,
        timestamp: new Date().toISOString(),
        agent_result: result
      };
      setMessages(prev => [...prev, assistantMsg]);
    }
    
    // Refresh credit status
    await fetchCreditStatus();
    
    showToast.success(`${agentData.agent_name} tamamlandı! ${agentData.credits_used} kredi kullanıldı.`);
  }, [currentSession, user, createSession, setCurrentSession, setMessages, fetchCreditStatus]);
  
  // Handle Research Cancel
  const handleResearchCancel = useCallback(async (elapsedSeconds) => {
    try {
      // Remove loading animation
      setMessages(prev => prev.filter(msg => !msg.isLoading && !msg.isResearchLoading));
      
      // Check if within 5 seconds
      if (elapsedSeconds <= 5 && researchCreditsUsed > 0) {
        // Refund credits
        const token = localStorage.getItem('access_token');
        const response = await fetch('/api/credits/refund', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            user_id: user.id,
            amount: researchCreditsUsed,
            reason: 'research_cancelled_early'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          showToast.success(`Araştırma iptal edildi. ${researchCreditsUsed} kredi iade edildi.`);
          await fetchCreditStatus();
        } else {
          showToast.warning('Araştırma iptal edildi.');
        }
      } else {
        showToast.info('Araştırma iptal edildi. (5 saniye geçtiği için kredi iadesi yapılamadı)');
      }
      
      // Reset research state
      setResearchStartTime(null);
      setResearchCreditsUsed(0);
      
    } catch (error) {
      console.error('Error cancelling research:', error);
      showToast.error('İptal işlemi başarısız oldu.');
    }
  }, [user, researchCreditsUsed, setMessages, fetchCreditStatus]);
  
  // Send Message Handler
  const handleSendMessage = useCallback(async () => {
    // Check if user is logged in
    if (!user) {
      showToast.info("Lütfen giriş yapın");
      setShowAuthModal(true);
      return;
    }
    
    // Check credit before sending (only for registered users, not guests)
    const isGuest = user.email?.includes('guest_');
    if (!isGuest && creditStatus && creditStatus.total_available <= 0) {
      showToast.error("Krediniz yetersiz. Kredi paketi satın alabilirsiniz.");
      setShowCreditLimitModal(true);
      return;
    }
    
    // Check if session exists, if not create one
    if (!currentSession) {
      const newSession = await createSession(user.id, 'Yeni Sohbet', 'chat');
      setCurrentSession(newSession);
    }
    
    if (!input.trim() || loading) return;
    
    if (selectedImage) {
      // Handle image message
      const userMessage = input;
      setInput("");
      
      const tempUserMsg = {
        id: Date.now().toString(),
        role: "user",
        content: `${userMessage} [Resim: ${selectedImage.name}]`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, tempUserMsg]);
      
      try {
        const formData = new FormData();
        formData.append('file', selectedImage);
        
        const response = await axios.post(
          `${API}/chat/image-analyze?user_id=${user.id}&session_id=${currentSession.id}&message=${encodeURIComponent(userMessage)}&model=${model}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        const assistantMsg = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMsg]);
        
        setSelectedImage(null);
        toast.success("Görsel analiz tamamlandı!");
      } catch (error) {
        toast.error("Görsel analiz başarısız oldu");
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      }
      return;
    }
    
    // Regular text message with streaming
    const userMessage = input;
    setInput("");
    setIsTyping(true);
    
    const tempUserMsg = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const response = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          session_id: currentSession.id,
          message: userMessage,
          model: model,
          use_library: useLibrary
        })
      });
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantResponse = "";
      const tempMessageId = Date.now().toString();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.chunk) {
                assistantResponse += data.chunk;
                if (isTyping) setIsTyping(false);
                
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  
                  if (lastMsg && lastMsg.role === 'assistant') {
                    newMessages[newMessages.length - 1] = {
                      ...lastMsg,
                      content: assistantResponse
                    };
                  } else {
                    newMessages.push({
                      id: tempMessageId,
                      role: "assistant",
                      content: assistantResponse,
                      timestamp: new Date().toISOString()
                    });
                  }
                  return newMessages;
                });
              }
              
              if (data.error) {
                if (data.credit_error) {
                  if (data.guest_limit) {
                    // Misafir limit aşıldı - kayıt modalı göster
                    toast.info("Misafir kullanım limitinize ulaştınız. Devam etmek için kayıt olun.");
                    setShowAuthModal(true);
                    setAuthTab("register");
                  } else {
                    // Kredi yetersiz
                    setCreditError(data);
                    setShowCreditLimitModal(true);
                  }
                  setMessages(prev => prev.filter(m => m.content.trim() !== ""));
                  setIsTyping(false);
                  break;
                }
                toast.error("Bir hata oluştu: " + data.error);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }
      
      // Refresh credit status
      fetchCreditStatus();
      
      // İlk mesajsa session title'ı AI ile güncelle
      if (messages.length === 0 && currentSession && assistantResponse) {
        try {
          // AI'dan kısa başlık üret
          const titleRes = await axios.post(`${API}/chat/generate-title`, {
            user_message: userMessage,
            assistant_response: assistantResponse.slice(0, 300),
          });
          const aiTitle = titleRes.data?.title || userMessage.slice(0, 50);
          
          await axios.patch(`${API}/sessions/${currentSession.id}/title?title=${encodeURIComponent(aiTitle)}`);
          setSessions(prev => prev.map(s => 
            s.id === currentSession.id ? { ...s, title: aiTitle } : s
          ));
          setCurrentSession(prev => ({ ...prev, title: aiTitle }));
        } catch (err) {
          // Fallback: mesajın kendisini kullan
          try {
            const fallbackTitle = userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : '');
            await axios.patch(`${API}/sessions/${currentSession.id}/title?title=${encodeURIComponent(fallbackTitle)}`);
            setSessions(prev => prev.map(s => 
              s.id === currentSession.id ? { ...s, title: fallbackTitle } : s
            ));
            setCurrentSession(prev => ({ ...prev, title: fallbackTitle }));
          } catch {}
        }
      }
      
      // Reload sessions
      const sessionsResponse = await axios.get(`${API}/sessions/${user.id}?source=chat`);
      setSessions(sessionsResponse.data);
      
    } catch (error) {
      showToast.error("Mesaj gönderilemedi");
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setIsTyping(false);
    }
  }, [input, currentSession, loading, selectedImage, user, model, isTyping]);
  
  // Realtime Voice Handlers
  const handleRealtimeTranscript = useCallback((transcript) => {
    if (transcript.partial) {
      // Kısmi transcript - gösterme (opsiyonel: typing indicator)
      return;
    }
    
    // Tam transcript - chat'e ekle
    const newMessage = {
      id: Date.now().toString(),
      role: transcript.role,
      content: transcript.text,
      timestamp: new Date().toISOString(),
      type: 'realtime_voice'
    };

    setMessages(prev => [...prev, newMessage]);
    
    console.log(`💬 ${transcript.role}: ${transcript.text}`);
  }, [setMessages]);

  // Handle audio chunks from realtime voice
  const handleAudioReceived = useCallback((audioChunks) => {
    // Store audio chunks temporarily - will be attached to next assistant message
    setPendingAudioChunks(audioChunks);
  }, []);

  // Effect to attach pending audio chunks to new assistant messages
  useEffect(() => {
    if (pendingAudioChunks && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage && lastMessage.role === 'assistant' && !messageAudioChunks[lastMessage.id]) {
        setMessageAudioChunks(prev => ({
          ...prev,
          [lastMessage.id]: pendingAudioChunks
        }));
        setPendingAudioChunks(null);
      }
    }
  }, [messages, pendingAudioChunks, messageAudioChunks]);

  // Replay audio for a specific message
  const replayMessageAudio = useCallback(async (messageId) => {
    const audioChunks = messageAudioChunks[messageId];
    if (!audioChunks || audioChunks.length === 0) {
      console.log('❌ No audio to replay for message:', messageId);
      showToast.info('Bu mesaj için ses kaydı bulunamadı');
      return;
    }

    try {
      setIsReplaying(messageId);
      console.log('🔄 Replaying message audio, chunks:', audioChunks.length);

      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });

      // Resume if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Process and play all chunks
      for (const base64Audio of audioChunks) {
        // Base64 → ArrayBuffer
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Int16 PCM → Float32
        const int16Array = new Int16Array(bytes.buffer);
        const float32Array = new Float32Array(int16Array.length);
        for (let i = 0; i < int16Array.length; i++) {
          float32Array[i] = int16Array[i] / 32768.0;
        }

        // AudioBuffer oluştur
        const audioBuffer = audioContext.createBuffer(
          1, // mono
          float32Array.length,
          24000 // sample rate
        );
        audioBuffer.getChannelData(0).set(float32Array);

        // Play chunk
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);
        
        // Wait for this chunk to finish before playing next
        await new Promise((resolve) => {
          source.onended = resolve;
          source.start();
        });
      }

      setIsReplaying(null);
      console.log('✅ Message audio replay completed');

    } catch (error) {
      console.error('Replay error:', error);
      setIsReplaying(null);
      showToast.error('Ses oynatılırken hata oluştu');
    }
  }, [messageAudioChunks]);

  // Voice Toggle Handler
  const handleVoiceToggle = useCallback(() => {
    if (user?.email?.includes('guest_')) {
      toast.info("Bu özellikleri kullanmak için Kayıt Olmanız Gerekmektedir");
      setShowAuthModal(true);
      setAuthTab("register");
      return;
    }
    
    setRealtimeMode(!realtimeMode);
    toast.info(realtimeMode ? "Sesli mod kapatıldı" : "Sesli mod aktif");
  }, [realtimeMode, user]);
  
  // Feedback Handler
  const handleGiveFeedback = useCallback(async (messageId, rating) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/feedback`, {
        message_id: messageId,
        session_id: currentSession?.id,
        rating: rating,
        feedback_text: null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      showToast.success(
        rating === 'positive' 
          ? "Teşekkürler! Zeha öğrenmeye devam ediyor 🧠" 
          : "Geri bildiriminiz alındı. Zeha kendini geliştiriyor 🚀"
      );
    } catch (error) {
      console.error('Feedback error:', error);
      showToast.error("Geri bildirim gönderilemedi");
    }
  }, [user, currentSession]);
  
  // Auth Handlers
  const handleLogin = useCallback(async (email, password) => {
    try {
      await login(email, password);
      setShowAuthModal(false);
      showToast.success("Giriş başarılı!");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Giriş başarısız";
      showToast.error(errorMessage);
    }
  }, [login]);
  
  const handleRegister = useCallback(async (name, email, password) => {
    try {
      await register(name, email, password, 'adult');
      setShowAuthModal(false);
      showToast.success("Kayıt başarılı!");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Kayıt başarısız";
      showToast.error(errorMessage);
    }
  }, [register]);
  
  const handleGuestLogin = useCallback(async () => {
    try {
      await guestLogin();
      setShowAuthModal(false);
      showToast.success("Misafir olarak giriş yaptınız");
    } catch (error) {
      showToast.error("Giriş başarısız");
    }
  }, [guestLogin]);
  
  // Logout Handler
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      showToast.success("Çıkış yapıldı");
      // Reload page to clear all state
      window.location.href = '/';
    } catch (error) {
      showToast.error("Çıkış yapılamadı");
    }
  }, [logout]);
  
  // Delete Session Handler
  const handleDeleteSession = useCallback(async () => {
    if (!sessionToDelete) return;
    try {
      await deleteSessionContext(sessionToDelete);
      setShowDeleteModal(false);
      setSessionToDelete(null);
      showToast.success("Sohbet silindi");
    } catch (error) {
      showToast.error("Sohbet silinemedi");
    }
  }, [sessionToDelete, deleteSessionContext]);
  
  // Delete Account Handler
  const handleDeleteAccount = useCallback(async () => {
    try {
      await axios.delete(`${API}/user/${user.id}`);
      logout();
      showToast.success("Hesabınız silindi");
    } catch (error) {
      showToast.error("Hesap silinemedi");
      throw error;
    }
  }, [user, logout]);
  
  // Credit Purchase Success Handler
  const handlePurchaseSuccess = useCallback((data) => {
    showToast.success(`🎉 ${data.credits_added?.toLocaleString() || ''} kredi hesabınıza eklendi!`);
    fetchCreditStatus();
  }, [fetchCreditStatus]);
  
  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar - Conditional */}
      {isSidebarOpen && (
        <Sidebar 
          sessions={sessions}
          currentSession={currentSession}
          onSelectSession={(session) => {
            setCurrentSession(session);
            loadMessages(session.id);
            setIsSidebarOpen(false);
          }}
          onNewChat={() => createSession(user.id, 'Yeni Sohbet', 'chat')}
          onDeleteSession={(sessionId) => {
            setSessionToDelete(sessionId);
            setShowDeleteModal(true);
          }}
          onRenameSession={renameSession}
          onAgentResult={handleAgentResult}
          onOpenCreditModal={() => setShowCreditModal(true)}
          user={user}
          creditStatus={creditStatus}
          unreadCount={unreadCount}
          onCreditsClick={() => setShowCreditModal(true)}
          onSubscriptionClick={() => navigate('/subscription')}
          onNotificationsClick={() => setShowNotificationsModal(true)}
          onProfileClick={() => {
            // Guest users should see auth modal, registered users see account modal
            if (user && !user.email?.includes('guest') && user.user_type !== 'guest') {
              setShowAccountModal(true);
            } else {
              setShowAuthModal(true);
            }
          }}
          onSettingsClick={() => setShowPersonalizationModal(true)}
          onPrivacyClick={() => setShowPrivacySettingsModal(true)}
          onSupportClick={() => setShowSupportModal(true)}
          onParentPanelClick={() => navigate('/parent/dashboard')}
          onSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          onLogout={handleLogout}
        />
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col relative">
        {/* Floating Hamburger Button (when sidebar closed) */}
        {!isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="absolute top-4 left-4 z-10 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            aria-label="Menüyü aç"
          >
            <Menu className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          </button>
        )}
        
        {/* Messages */}
        <ChatMessages
          messages={messages}
          isTyping={isTyping}
          favorites={favorites}
          isKidsUser={isKidsUser}
          searchQuery={searchQuery}
          onCopyMessage={(content, messageId) => {
            navigator.clipboard.writeText(content);
            showToast.success('Kopyalandı!');
          }}
          onToggleFavorite={toggleFavoriteContext}
          onGiveFeedback={handleGiveFeedback}
          onImageClick={(imageUrl) => {
            setViewerImage(imageUrl);
            setIsViewerOpen(true);
          }}
          onResearchCancel={handleResearchCancel}
          onReplayAudio={replayMessageAudio}
          messageAudioChunks={messageAudioChunks}
          isReplaying={isReplaying}
          messagesEndRef={messagesEndRef}
          onSuggestionClick={(text) => {
            setInput(text);
          }}
        />
        
        {/* Input */}
        <ChatInput
          input={input}
          loading={loading}
          selectedImage={selectedImage}
          selectedFile={selectedFile}
          isVoiceMode={realtimeMode}
          creditLimitReached={creditStatus?.remaining_monthly_credits <= 0}
          isKidsUser={isKidsUser}
          creditStatus={creditStatus}
          useLibrary={useLibrary}
          onInputChange={setInput}
          onSend={handleSendMessage}
          onImageSelect={(file) => setSelectedImage(file)}
          onFileSelect={(file) => setSelectedFile(file)}
          onVoiceToggle={handleVoiceToggle}
          onClearAttachment={() => {
            setSelectedImage(null);
            setSelectedFile(null);
          }}
          onAgentExecute={handleAgentResult}
          onOpenCreditModal={() => setShowCreditModal(true)}
          onLibraryToggle={() => setUseLibrary(!useLibrary)}
        />
        
        {/* Realtime Voice Component - Only when active and for registered users */}
        {realtimeMode && user && !user.email?.includes('guest') && user.user_type !== 'guest' && (
          <RealtimeVoice
            user={user}
            session={currentSession}
            context={{
              topic: currentSession?.title || 'Genel Sohbet',
              grade: 8,
              subject: 'Genel'
            }}
            onTranscript={handleRealtimeTranscript}
            onAudioReceived={handleAudioReceived}
            onClose={() => setRealtimeMode(false)}
            onCreditDeducted={async () => {
              // Kredi düşümü - her Zeha cevabı için
              await fetchCreditStatus();
            }}
          />
        )}
      </div>
      
      {/* Modals */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        authTab={authTab}
        onTabChange={setAuthTab}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onGuestLogin={handleGuestLogin}
        loading={authLoading}
      />
      
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        supportTab={supportTab}
        onTabChange={setSupportTab}
        user={user}
      />
      
      <NotificationsModal
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        notifications={notifications}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={() => markAllAsRead(user?.id)}
      />
      
      <CreditLimitModal
        isOpen={showCreditLimitModal}
        onClose={() => setShowCreditLimitModal(false)}
        creditStatus={creditStatus}
        user={user}
      />
      
      <PrivacySettingsModal
        isOpen={showPrivacySettingsModal}
        onClose={() => setShowPrivacySettingsModal(false)}
        user={user}
      />
      
      <AccountModal
        isOpen={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        user={user}
        onDeleteAccount={handleDeleteAccount}
      />
      
      <PersonalizationModal
        isOpen={showPersonalizationModal}
        onClose={() => setShowPersonalizationModal(false)}
        language={language}
        onLanguageChange={changeLanguage}
      />
      
      <ImageViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        imageUrl={viewerImage}
      />
      
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSessionToDelete(null);
        }}
        onConfirm={handleDeleteSession}
        title="Sohbeti Sil"
        description="Bu sohbeti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
      />
      
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onPurchaseSuccess={handlePurchaseSuccess}
        user={user}
        onOpenAuthModal={(tab) => {
          setAuthTab(tab);
          setShowAuthModal(true);
        }}
      />
    </div>
  );
}

/**
 * ChatPage - Wrapper component with context providers
 */
export default function ChatPage() {
  return (
    <AuthProvider>
      <ChatProvider>
        <CreditProvider>
          <NotificationProvider>
            <ChatPageContent />
          </NotificationProvider>
        </CreditProvider>
      </ChatProvider>
    </AuthProvider>
  );
}
